import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { api, ApiError } from './api';
import { Button } from './ui';
import type { HouseholdProfile } from './screens/Household';
import { UpdateNotice } from './UpdateNotice';
const Week = lazy(() => import('./screens/Week').then(module => ({ default: module.Week })));
const Recipes = lazy(() => import('./screens/Recipes').then(module => ({ default: module.Recipes })));
const ShoppingList = lazy(() => import('./screens/ShoppingList').then(module => ({ default: module.ShoppingList })));
import { Ideas } from './screens/Ideas';
const Upload = lazy(() => import('./screens/Upload').then(module => ({ default: module.Upload })));
const Household = lazy(() => import('./screens/Household').then(module => ({ default: module.Household })));
import { InstallBanner } from './InstallBanner';
import { applyTheme, readTheme, watchSystemTheme } from './theme';

type Tab = 'week' | 'list' | 'recipes' | 'upload' | 'ideas' | 'settings';

function cachedProfile(): HouseholdProfile | null {
  try {
    const raw = window.localStorage.getItem('fmp-household-profile');
    if (!raw) return null;
    const data = JSON.parse(raw) as HouseholdProfile;
    return typeof data.setup_complete === 'boolean' && typeof data.name === 'string' ? data : null;
  } catch { return null; }
}

/**
 * Inline SVG rather than emoji glyphs: the OS renders emoji in its own colors,
 * which ignores the active/inactive state and looks like four unrelated
 * stickers. These inherit currentColor and stay consistent across platforms.
 */
function Icon({ name }: { name: Tab }) {
  const common = {
    width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.75,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'week':
      return (
        <svg {...common}>
          <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
          <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
        </svg>
      );
    case 'list':
      return (
        <svg {...common}>
          <path d="M9 6h11M9 12h11M9 18h11" />
          <path d="M4 6l1 1 1.5-2M4 12l1 1 1.5-2M4 18l1 1 1.5-2" />
        </svg>
      );
    case 'recipes':
      return (
        <svg {...common}>
          <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v18H5.5A1.5 1.5 0 0 1 4 19.5z" />
          <path d="M8 3v18" />
        </svg>
      );
    case 'upload':
      return (
        <svg {...common}>
          <path d="M12 16V4.5M12 4.5 7.5 9M12 4.5 16.5 9" />
          <path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" />
        </svg>
      );
    case 'ideas':
      return (
        <svg {...common}>
          <path d="M12 3v1.5M12 19.5V21M4.2 7.5l1.3.75M18.5 15.75l1.3.75M4.2 16.5l1.3-.75M18.5 8.25l1.3-.75" />
          <circle cx="12" cy="12" r="4.5" />
        </svg>
      );
    case 'settings':
      return null;
  }
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'week', label: 'Week' },
  { id: 'list', label: 'Shopping' },
  { id: 'recipes', label: 'Recipes' },
  { id: 'ideas', label: 'Ideas' },
  { id: 'upload', label: 'Upload' },
];

/**
 * Bottom tab navigation, because the two contexts of use are standing in a
 * kitchen and pushing a cart (plan section 10) -- reachable with a thumb, one
 * handed, without moving your grip.
 *
 * The hash is the route, so a reload lands you back where you were and the
 * back button behaves. Not worth a router dependency for five screens.
 */
export function App() {
  const [tab, setTab] = useState<Tab>(() => readHash());
  const [listId, setListId] = useState<string | null>(null);
  const [profile, setProfile] = useState<HouseholdProfile | null>(cachedProfile);
  const [profileError, setProfileError] = useState<string | null>(null);

  const refreshProfile = useCallback(async () => {
    try {
      const data = await api.get<HouseholdProfile>('/household');
      setProfile(data); setProfileError(null);
      try { window.localStorage.setItem('fmp-household-profile', JSON.stringify(data)); } catch { /* storage may be unavailable */ }
    } catch (error) {
      setProfileError(error instanceof ApiError ? error.message : 'Could not load your household.');
      throw error;
    }
  }, []);

  useEffect(() => { void refreshProfile().catch(() => undefined); }, [refreshProfile]);

  useEffect(() => {
    const onHash = () => setTab(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  /**
   * The class is already correct -- the inline script in index.html set it
   * before paint. This only keeps the status-bar color in step and follows the
   * system if the choice is "system", so changing the phone's appearance at
   * sunset moves the app with it instead of waiting for a reload.
   */
  useEffect(() => {
    applyTheme(readTheme());
    return watchSystemTheme(readTheme);
  }, []);

  function go(next: Tab) {
    setTab(next);
    window.location.hash = next;
  }

  if (profileError && !profile) return <div className="mx-auto max-w-lg p-6 text-text-primary">
    <p role="alert" className="mb-4">{profileError}</p>
    <Button onClick={() => void refreshProfile().catch(() => undefined)}>Try again</Button>
  </div>;

  if (!profile) return <p role="status" className="p-6 text-text-muted">Loading your household…</p>;

  if (!profile.setup_complete) return <Suspense fallback={<p className="p-6 text-text-muted">Loading setup…</p>}>
    <Household profile={profile} onRefresh={refreshProfile}
      onComplete={(next) => { void refreshProfile().then(() => go(next)).catch(() => undefined); }} />
  </Suspense>;

  return (
    <>
      <InstallBanner />
      <UpdateNotice />
      <Suspense fallback={<p role="status" className="mx-auto max-w-3xl px-5 py-8 text-text-muted">Loading…</p>}>

      {tab === 'week' && <Week onOpenList={(id) => { setListId(id); go('list'); }} />}
      {tab === 'list' && <ShoppingList listId={listId} />}
      {tab === 'recipes' && <Recipes />}
      {tab === 'upload' && <Upload onSettings={() => go('settings')} />}
      {tab === 'ideas' && <Ideas />}
      {tab === 'settings' && <Household profile={profile} onRefresh={refreshProfile}
        onBack={() => go('upload')} onComplete={() => go('upload')} />}
      </Suspense>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border-subtle
                   safe-bottom-nav bg-bg-raised/90 backdrop-blur-lg"
        aria-label="Main"
      >
        <ul className="mx-auto flex max-w-lg">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <li key={t.id} className="flex-1">
                <button
                  type="button"
                  onClick={() => go(t.id)}
                  aria-current={active ? 'page' : undefined}
                  className={`relative flex w-full flex-col items-center gap-1 pb-2.5 pt-3
                              font-display text-[9px] uppercase tracking-[0.1em] transition-colors
                              ${active ? 'text-accent-vibrant' : 'text-text-muted'}`}
                >
                  {/* A hard 2px bar rather than a filled pill: the active state
                      reads instantly in peripheral vision without adding a box. */}
                  <span
                    aria-hidden
                    className={`absolute inset-x-5 top-0 h-[2px] rounded-full transition-opacity
                                ${active ? 'bg-accent-vibrant opacity-100' : 'opacity-0'}`}
                  />
                  <Icon name={t.id} />
                  {t.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}

function readHash(): Tab {
  const raw = window.location.hash.replace('#', '');
  return (['week', 'list', 'recipes', 'upload', 'ideas', 'settings'] as const).includes(raw as Tab)
    ? (raw as Tab)
    : 'week';
}
